import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import toast from 'helpers/toast';

import { getPremiumTrialSubscriptionIdFromPlan } from '../helpers';
import { PREMIUM_PLAN_ID } from 'components/billing/constants';
import useDetailedBillingQuery from '../../useDetailedBillingQuery';
import { PREMIUM_TRIAL_ELIGIBLE } from './usePremiumTrialEligible';
import { translate } from 'javascripts/translations/i18n';

const BILLING_SAMPLE_PLAN_MUTATION = gql`
  mutation BillingSamplePlanMutation($input: BillingSamplePlanMutationInput!) {
    billingSamplePlanMutation(input: $input) {
      trialSuccessful
      errors {
        error
        message
      }
    }
  }
`;

const useSamplePremiumPlan = ({ onSuccess, onError }) => {
  const {
    startPolling,
    stopPolling,
    availableBillingPlans,
    subscription,
    currentPlan,
  } = useDetailedBillingQuery();
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (isPolling && currentPlan?.identifier === PREMIUM_PLAN_ID) {
      stopPolling();
      setIsPolling(false);
      onSuccess();
      toast.success(
        translate('billing.v2.premiumTrial.premiumTrialStartActivated'),
      );
    }
  }, [isPolling, onSuccess, currentPlan?.identifier, stopPolling]);

  useEffect(() => {
    return stopPolling;
  }, [stopPolling]);

  const [samplePremiumPlanMutation, { loading }] = useMutation(
    BILLING_SAMPLE_PLAN_MUTATION,
    {
      refetchQueries: [PREMIUM_TRIAL_ELIGIBLE],
      update: (_, { data }) => {
        const errors = data.billingSamplePlanMutation.errors;

        if (errors.length) {
          onError(errors[0].message);
          return;
        }

        if (!data.billingSamplePlanMutation.trialSuccessful) {
          onError(translate('billing.v2.premiumTrial.trialStartError'));
          return;
        }

        startPolling(100);
        setIsPolling(true);
      },
      error: () => {
        onError(translate('billing.v2.premiumTrial.trialStartError'));
      },
    },
  );

  const samplePremiumPlan = async () => {
    const subscriptionOptionId = getPremiumTrialSubscriptionIdFromPlan(
      availableBillingPlans,
    );

    await samplePremiumPlanMutation({
      variables: {
        input: {
          id: subscription.id,
          subscriptionOptionId,
        },
      },
    });
  };

  return {
    samplePremiumPlan,
    loading: loading || isPolling,
  };
};

export default useSamplePremiumPlan;
